import { useMutation, useQueryClient } from 'react-query';
import { CommentFilterType } from '~/containers/Comments/Comments';
import { addReaction, removeReaction } from '~/api/reactions';
import { Comment } from '~/types/comment';

interface ReactionMutationVariables {
  commentId: string;
  reactionName: string | null;
  isRemoving: boolean;
  parentCommentId?: string;
}

interface SnapshotEntry {
  key: unknown[];
  data: unknown;
}

function applyReactionToComment(
  comment: Comment,
  commentId: string,
  reactionName: string | null,
  isRemoving: boolean,
  currentUserId: string
): Comment {
  if (comment.uuid !== commentId) return comment;

  const current: Record<string, string[]> = {
    ...(comment.byReactionUserIds || {})
  };

  if (isRemoving || reactionName === null) {
    const oldKey = comment.myReaction;
    if (oldKey && current[oldKey]) {
      current[oldKey] = current[oldKey].filter((id) => id !== currentUserId);
    }
    return {
      ...comment,
      myReaction: undefined,
      reactionsCount: Math.max(0, (comment.reactionsCount || 1) - 1),
      byReactionUserIds: current
    };
  }

  // Adding or switching
  const oldKey = comment.myReaction;
  if (oldKey && oldKey !== reactionName && current[oldKey]) {
    current[oldKey] = current[oldKey].filter((id) => id !== currentUserId);
  }
  const existing = current[reactionName] || [];
  current[reactionName] = existing.includes(currentUserId)
    ? existing
    : [...existing, currentUserId];

  const newCount = oldKey
    ? comment.reactionsCount || 0  // switching — no change
    : (comment.reactionsCount || 0) + 1;  // new reaction

  return {
    ...comment,
    myReaction: reactionName,
    reactionsCount: newCount,
    byReactionUserIds: current
  };
}

function updateCommentInPages(
  pages: Comment[][],
  commentId: string,
  reactionName: string | null,
  isRemoving: boolean,
  currentUserId: string,
  parentCommentId?: string
): Comment[][] {
  return pages.map((page) =>
    page.map((comment) => {
      // If a parentCommentId was given, we're updating a child reply
      if (parentCommentId) {
        if (comment.uuid !== parentCommentId) return comment;
        return {
          ...comment,
          children: (comment.children || []).map((child) =>
            applyReactionToComment(child, commentId, reactionName, isRemoving, currentUserId)
          )
        };
      }
      // Otherwise update top-level comment
      const updated = applyReactionToComment(
        comment,
        commentId,
        reactionName,
        isRemoving,
        currentUserId
      );
      // Also check children even without parentCommentId
      if (updated === comment && comment.children?.length) {
        return {
          ...comment,
          children: (comment.children || []).map((child) =>
            applyReactionToComment(child, commentId, reactionName, isRemoving, currentUserId)
          )
        };
      }
      return updated;
    })
  );
}

function updateCommentInFlatArray(
  comments: Comment[],
  commentId: string,
  reactionName: string | null,
  isRemoving: boolean,
  currentUserId: string,
  parentCommentId?: string
): Comment[] {
  return comments.map((comment) => {
    if (parentCommentId) {
      if (comment.uuid !== parentCommentId) return comment;
      return {
        ...comment,
        children: (comment.children || []).map((child) =>
          applyReactionToComment(child, commentId, reactionName, isRemoving, currentUserId)
        )
      };
    }
    const updated = applyReactionToComment(
      comment,
      commentId,
      reactionName,
      isRemoving,
      currentUserId
    );
    if (updated === comment && comment.children?.length) {
      return {
        ...comment,
        children: (comment.children || []).map((child) =>
          applyReactionToComment(child, commentId, reactionName, isRemoving, currentUserId)
        )
      };
    }
    return updated;
  });
}

export function useReactToCommentMutation(
  commentType: CommentFilterType,
  buildingId: string,
  selectedGroupId: string,
  selectedGatheringId: string,
  gatheringDate: string,
  currentUserId: string,
  creatorEmail?: string
) {
  const queryClient = useQueryClient();

  const chatKey = ['chat-comments', buildingId];
  const groupKey = ['group-comments', buildingId, selectedGroupId];
  const gatheringKey = [
    'gathering-comments',
    buildingId,
    selectedGatheringId,
    gatheringDate
  ];

  return useMutation(
    async ({ commentId, reactionName, isRemoving, parentCommentId }: ReactionMutationVariables) => {
      if (isRemoving || reactionName === null) {
        await removeReaction(commentId, commentType, parentCommentId);
      } else {
        await addReaction(commentId, reactionName, commentType, parentCommentId, creatorEmail);
      }
    },
    {
      onMutate: async ({ commentId, reactionName, isRemoving, parentCommentId }) => {
        const snapshots: SnapshotEntry[] = [];

        const keys = [chatKey, groupKey, gatheringKey];
        for (const key of keys) {
          await queryClient.cancelQueries(key);
        }

        // Infinite query caches (chat + group)
        for (const key of [chatKey, groupKey]) {
          const prev = queryClient.getQueryData<{ pages: Comment[][] }>(key);
          if (!prev) continue;
          snapshots.push({ key, data: prev });
          queryClient.setQueryData<{ pages: Comment[][] }>(key, (old) => {
            if (!old) return old as any;
            return {
              ...old,
              pages: updateCommentInPages(
                old.pages,
                commentId,
                reactionName,
                isRemoving,
                currentUserId,
                parentCommentId
              )
            };
          });
        }

        // Flat array cache (gathering)
        const prevGathering = queryClient.getQueryData<Comment[]>(gatheringKey);
        if (prevGathering) {
          snapshots.push({ key: gatheringKey, data: prevGathering });
          queryClient.setQueryData<Comment[]>(gatheringKey, (old) => {
            if (!old) return old as any;
            return updateCommentInFlatArray(
              old,
              commentId,
              reactionName,
              isRemoving,
              currentUserId,
              parentCommentId
            );
          });
        }

        return { snapshots };
      },

      onError: (_err, _vars, context: any) => {
        if (!context?.snapshots) return;
        for (const { key, data } of context.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
    }
  );
}
